/*
 * Setup workspace — shared helpers (presentation-only scaffolding).
 *
 * The per-entity CRUD *logic* (exact fetch URLs, methods, JSON bodies,
 * validation branches, auto-fill rules) lives in ./entities/* and is copied
 * verbatim from the original XControl/XForm pairs. These helpers only remove
 * the boilerplate of building the same fetch options object; they do NOT change
 * any URL, method, header, or body.
 */

export const apiURL = process.env.REACT_APP_BACKEND;

/*
 * Dirty-tracking for the master-detail editors. A draft carries a `_orig`
 * snapshot taken when it's first opened (via withOrig); isDraftDirty compares
 * the CURRENT field values to that snapshot — a true value comparison, not a
 * "touched" flag — so editing a field and then reverting it back to its
 * original clears the dirty state instead of leaving a false positive. Values
 * are string-normalized (so a dropdown going number→string→back reads clean),
 * and internal/derived keys are excluded.
 */
// `_row`/`_orig` are bookkeeping; `companies` is a DERIVED option list some catalogue drafts stash
// for their company dropdown (not a user-editable value) — excluded so it can't flip the dirty flag.
const DIRTY_OMIT = ['_row', '_orig', 'companies'];
export function draftSnapshot(draft) {
  if (!draft) return '';
  return JSON.stringify(
    Object.keys(draft)
      .filter((k) => DIRTY_OMIT.indexOf(k) === -1)
      .sort()
      .map((k) => [k, draft[k] == null ? null : String(draft[k])])
  );
}
export function withOrig(draft) {
  return { ...draft, _orig: draftSnapshot(draft) };
}
export function isDraftDirty(draft) {
  return !!draft && draft._orig != null && draftSnapshot(draft) !== draft._orig;
}

/*
 * Tree search shared by the setup trees. Filter a built tree (nodes carry {id, name,
 * children}) down to the paths that reach a case-insensitive name match, and return the
 * set of ancestor ids to auto-expand so each match is visible. A node that itself matches
 * keeps its whole subtree; a node kept only because a descendant matched keeps just the
 * matching branches.
 */
export function filterTree(nodes, query) {
  const needle = query.trim().toLowerCase();
  const expand = new Set();
  const walk = (list) => {
    const out = [];
    (list || []).forEach((n) => {
      const selfMatch = typeof n.name === 'string' && n.name.toLowerCase().includes(needle);
      const keptKids = selfMatch ? (n.children || []) : walk(n.children);
      if (!selfMatch && keptKids.length === 0) return;
      if (keptKids.length) expand.add(n.id);
      out.push(n.children ? { ...n, children: keptKids } : n);
    });
    return out;
  };
  return { nodes: walk(nodes), expand };
}
// All ids of nodes that have children — to auto-expand a whole (filtered) tree, e.g. when
// a status/role filter is active so its matches read flat without expanding by hand.
export function expandableIds(nodes) {
  const ids = new Set();
  const walk = (list) => (list || []).forEach((n) => { if (n.children && n.children.length) { ids.add(n.id); walk(n.children); } });
  walk(nodes);
  return ids;
}

/*
 * postJson — mirrors the original forms' create/update call shape exactly:
 *   fetch(apiURL + path, {method:'POST', headers:{Content-Type, Accept,
 *   Authorization}, credentials:'include', body: JSON.stringify(body)})
 *   .then(res => res.json())
 * Returns the parsed JSON (so callers can branch on res.error) — identical to
 * the originals.
 */
export function postJson(path, body, webToken) {
  return fetch(apiURL + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': webToken,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  }).then((res) => res.json());
}

/*
 * postRaw — like postJson but resolves the raw Response WITHOUT parsing JSON.
 * Mirrors AssignmentForm.handleSubmit, which treated any resolved response as
 * success (it never inspected the body). Kept separate so that verbatim
 * semantics are preserved for the assignment create call.
 */
export function postRaw(path, body, webToken) {
  return fetch(apiURL + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': webToken,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
    return res;
  });
}

/*
 * del — mirrors the original controls' delete call shape:
 *   fetch(apiURL + path, {method:'DELETE', headers:{Accept, Authorization},
 *   credentials:'include'})
 * (The originals varied slightly on including Content-Type on DELETE; it is a
 * no-op for a bodyless DELETE, so a single shape is behavior-equivalent.)
 */
export function del(path, webToken) {
  return fetch(apiURL + path, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': webToken,
    },
    credentials: 'include',
  }).then((res) => {
    if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
    return res;
  });
}

/*
 * moveGuardAssignment — the ONE safe guard↔gate assignment mutation, shared by the Gate
 * Assignments pane and the Users & Guards inline picker (item 1 [86bbwu3px]). The two used to
 * diverge: AssignmentsPane created-first while UsersPane DELETED first, so a rejected create in
 * the Users path left the guard with GateUser.GateAssignmentId = NULL → blocked at mobile login
 * ("Gate not active"). This is the single create-first / delete-second implementation.
 *
 *   - CREATE the new assignment FIRST; only if that succeeds do we DELETE the old one. A failed
 *     create therefore NEVER removes an existing assignment (no stranded guard).
 *   - FK fallback: use the gate's nested row when present, else its flat *Id column, so a gate
 *     that only carries GateSubscriberId/GateCustomerId/GateSiteId still assigns (no crash).
 *   - gate == null/undefined → just delete the existing assignment (unassign).
 *   - existingId falsy → create only (a brand-new guard being assigned during creation).
 */
export async function moveGuardAssignment({ gate, guardId, existingId, webToken }) {
  if (gate) {
    await postRaw('api/gateassignmentNew/', {
      GateId: gate.id,
      GateUserId: guardId,
      GateSubscriberId: gate.GateSubscriber ? gate.GateSubscriber.id : gate.GateSubscriberId,
      GateCustomerId: gate.GateCustomer ? gate.GateCustomer.id : gate.GateCustomerId,
      GateSiteId: gate.GateSite ? gate.GateSite.id : gate.GateSiteId,
      gateId: gate.id,
    }, webToken);
  }
  if (existingId) await del('api/gateassignmentdestroy/' + existingId, webToken);
}

/*
 * getJson — mirrors the original controls' association-check GET:
 *   fetch(apiURL + path, {method:'get', headers:{Accept, Authorization},
 *   credentials:'include'}) with res.ok guard, then res.json().
 */
export function getJson(path, webToken) {
  return fetch(apiURL + path, {
    method: 'get',
    headers: { 'Accept': 'application/json', 'Authorization': webToken },
    credentials: 'include',
  }).then((res) => {
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  });
}
